import {Injectable} from '@angular/core';
import { Validators } from '@angular/forms';
import {CommonInfoService} from '../../cart-process/common-info.service';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../../external-interfaces/notification-provider.service';
import {MainProcessTypeEnum} from '../../models/cartDetails/main-process-type.enum';
import { FormCustomValidators } from './form-custom-validators';
import {FormGroupsEnum} from './form-groups.enum';

export class ITemplateFormField {
  'input': ITemplateOptionsInput;
  'label': ITemplateOptionsLabel;
  'forceHide': boolean;
  'isEditable': boolean;
  'uid': string;
}

export class ITemplateOptionsLabel {
  '_isEditable' = false;
  '_label' = '';
  '_label-class' = 'form-label';
}

export class ITemplateOptionsInput {
  '_isEditable' = false;
  '_div-class': string = null;
  '_input-class': string = null;
  '_input-helper': string = null;
  '_validators': string[] = [];
  'type': string = null;
  'name': string = null;
  'value': string = null;
  'staticValue': string = null;
  'class': string = null;
  'maxlength': string = null;
  'placeholder': string = null;
  'autocomplete': string = null;
  'aria-required': string = null;
  'describedby': string = null;
  'data-automation-id': string = null;
  'uppercase': string = null;
  '[textMask]': string = null;
  '[(ngModel)]': string = null;
  '(change)': string = null;
  '(click)': string = null;
  'formControlGroup': string = null;
  'formControlName': string = null;
  'tabindex': string = null;
}


class TemplateOptionsDefault {
  setAttribute(name: any, value: any) {
    this[name] = value;
    return this;
  }
}

class TemplateOptionsLabel extends TemplateOptionsDefault {
  constructor(uid: string, params: ITemplateOptionsLabel) {
    super();

    const defaultValues = new ITemplateOptionsLabel();
    const allowedKeys = Object.keys(defaultValues);

    this.setAttribute('for', uid);

    if (!params['data-automation-id'] && params['_label-test-id']) {
      this.setAttribute('data-automation-id', params['_label-test-id']);
    }

    allowedKeys.forEach(key => {
      const val = params[key] || params[key] === '' || defaultValues[key];

      if ( val !== null ){
        this.setAttribute(key, val);
      }
    });
  }
}

class TemplateOptionsInput extends TemplateOptionsDefault {
  constructor(uid: string, params: ITemplateOptionsInput) {
    super();

    const defaultValues = new ITemplateOptionsInput();
    const allowedKeys = Object.keys(defaultValues);

    this.setAttribute('id', uid);

    if (!params['data-automation-id'] && params['_input-test-id']) {
      this.setAttribute('data-automation-id', params['_input-test-id']);
    }

    allowedKeys.forEach(key => {
      const val = params[key] || params[key] === '' || defaultValues[key];

      if ( val !== null ) {
        this.setAttribute(key, val);
      }
    });
  }
}

export class FieldForm {
  uid: string;
  label: object;
  input: object;
  isEditable = true;

  constructor(uid: string, params: any) {
    const config = Object.assign({}, params, {_isEditable: this.isEditable});

    this.uid = uid;
    this.label = new TemplateOptionsLabel(uid, config);
    this.input = new TemplateOptionsInput(uid, config);
  }
}

const firstName = new FieldForm('firstName', {
  '_label': 'Imię',
  '_label-test-id': 'contract-credential-section-first-name-label',
  '_input-test-id': 'contract-credential-section-first-name-field',
  'placeholder': 'Imię',
  'type': 'text',
  'name': 'firstName',
  'maxlength': '50',
  'aria-required': 'true',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'firstName',
  'staticValue' : 'firstName',
  '_validators': [Validators.required, FormCustomValidators.onlyLetters]
});

const lastName = new FieldForm('lastName', {
  '_label': 'Nazwisko',
  '_label-test-id': 'contract-credential-section-last-name-label',
  '_input-test-id': 'contract-credential-section-last-name-field',
  'placeholder': 'Nazwisko',
  'type': 'text',
  'name': 'lastName',
  'maxlength': '50',
  'aria-required': 'true',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'lastName',
  'staticValue' : 'lastName',
  '_validators': [Validators.required, FormCustomValidators.onlyLetters]
});

const pesel = new FieldForm('pesel', {
  '_label': 'PESEL',
  '_label-test-id': 'contract-credential-section-customer-code-label',
  '_input-test-id': 'contract-credential-section-customer-code-field',
  'placeholder': 'PESEL',
  'type': 'tel',
  'name': 'customerCode',
  'maxlength': '50',
  'aria-required': 'true',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'pesel',
  'staticValue' : 'pesel',
  '_validators': [Validators.required, FormCustomValidators.pesel]
});

const mIdNumber = new FieldForm('m-id-number', {
  '_label': 'Seria i numer mDowodu',
  '_label-test-id': 'contract-credential-section-id-m-number-label',
  '_input-test-id': 'contract-credential-section-id-m-number-field',
  '_div-class': 'form-input-with-hint',
  '_input-helper': '<small class="input-helper">Ważność mDowodu musi być dłuższa niż 7 dni.</small>',
  'placeholder': 'mObywatel',
  'type': 'text',
  'name': 'idSeriesNumber',
  'aria-describedby':'m-id-info',
  'aria-required': 'true',
  'uppercase': '',
  '[textMask]': '{mask: validationMasks.mIdNumber, guide: false}',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'mDocumentNo',
  '_validators': [Validators.required, FormCustomValidators.mPersonalIdNumber]
});

const idSeriesNumber = new FieldForm('id-number', {
  '_label': 'Seria i numer dowodu',
  '_label-test-id': 'contract-credential-section-id-card-number-label',
  '_input-test-id': 'contract-credential-section-id-card-number-field',
  '_div-class': 'form-input-with-hint',
  '_input-helper': '<small class="input-helper">Ważność dowodu osobistego musi być dłuższa niż 7 dni.</small>',
  'placeholder': 'Seria i numer dowodu',
  'type': 'text',
  'name': 'idSeriesNumber',
  'aria-describedby':'id-info',
  'aria-required': 'true',
  'uppercase': '',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'documentNo',
  '_validators': [Validators.required, FormCustomValidators.personalIdNumber]
});

const mIdExpiryDate = new FieldForm('m-id-expiry-date', {
  '_label': 'Data ważności mDowodu <span class="sr-only">w formacie DD.MM.RRRR</span>',
  '_label-test-id': 'contract-credential-section-m-id-expiry-date-label',
  '_input-test-id': 'contract-credential-section-m-id-expiry-date-field',
  '_div-class': 'input-with-helper input-with-buttons',
  '_input-class': 'input-business-start-date',
  '_input-helper': '<small class="input-helper">Do zrealizowania zamówienia online, data ważności mDowodu musi być dłuższa niż 7 dni.</small>',
  'placeholder': 'Data ważności mDowodu',
  'type': 'tel',
  'name': 'idExpiryDate',
  'aria-required': 'true',
  'aria-describedby': 'date-format',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'mIdExpiryDate',
  '_validators': [Validators.required, FormCustomValidators.validateExpiryDate],
});

const idExpiryDate = new FieldForm('id-expiry-date', {
  '_label': 'Data ważności dowodu osobistego <span class="sr-only">w formacie DD.MM.RRRR</span>',
  '_label-test-id': 'contract-credential-section-id-expiry-date-label',
  '_input-test-id': 'contract-credential-section-id-expiry-date-field',
  '_div-class': 'input-with-helper input-with-buttons',
  '_input-class': 'input-business-start-date',
  '_input-helper': '<small class="input-helper">Do zrealizowania zamówienia online, data ważności dowodu osobistego musi być dłuższa niż 7 dni.</small>',
  'placeholder': 'Data ważności dowodu osobistego',
  'type': 'tel',
  'name': 'idExpiryDate',
  'aria-required': 'true',
  'aria-describedby': 'date-format',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'idExpiryDate',
  '_validators': [Validators.required, FormCustomValidators.validateExpiryDate],
});

const indefiniteId = new FieldForm('indefinite-id', {
  '_input-test-id': 'contract-credential-section-indefinite-id-field',
  'name': 'indefiniteId',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'indefiniteId',
});

const passwordForContract = new FieldForm('password-contract', {
  '_label': 'Utwórz hasło do umowy',
  '_label-test-id': 'contract-credential-section-contract-password-label',
  '_input-test-id': 'contract-credential-section-contract-password-field',
  '_div-class': 'form-input-with-hint',
  'placeholder': 'Utwórz hasło do umowy',
  '_input-helper': function (commonInfo) {
    let text = '<small class="input-helper">Użyj od 6 do 9 liter i/lub cyfr. Hasło przyda się w rozmowach z konsultantami ';

    if (!commonInfo.isVirginBrand) {
      text += 'Play.';
    }

    text += '</small>';

    return text;
  },
  'type': 'text',
  'name': 'passwordForContract',
  'maxlength': '9',
  'aria-describedby': 'password-format',
  'aria-required': 'true',
  'uppercase': '',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'contractPassword',
  '_validators': [Validators.required, FormCustomValidators.password],
});

const companyName = new FieldForm('companyName', {
  '_label': 'Nazwa firmy',
  '_label-test-id': 'contract-credential-section-company-name-label',
  '_input-test-id': 'contract-credential-section-contract-company-name-field',
  'placeholder': 'Nazwa firmy',
  'type': 'text',
  'name': 'company',
  'maxlength': '128',
  'aria-required': 'true',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'companyName',
  '_validators': [Validators.required]
});

const nip = new FieldForm('nip', {
  '_label': 'NIP',
  '_label-test-id': 'contract-credential-section-nip-label',
  '_input-test-id': 'contract-credential-section-contract-nip-field',
  'placeholder': 'NIP',
  'type': 'tel',
  'name': 'companyNumber',
  'aria-required': 'true',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'nip',
  '_validators': [Validators.required, FormCustomValidators.nip],
});

const regon = new FieldForm('regon', {
  '_label': 'REGON',
  '_label-test-id': 'contract-credential-section-regon-label',
  '_input-test-id': 'contract-credential-section-contract-regon-field',
  'placeholder': 'REGON',
  'type': 'text',
  'name': 'regon',
  'aria-required': 'true',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'regon',
  '_validators': [Validators.required, FormCustomValidators.regon],
});

const legalizeDate = new FieldForm('business-start-date', {
  '_label': 'Data rozpoczęcia działalności <span class="sr-only">w formacie DD.MM.RRRR</span>',
  '_label-test-id': 'contract-credential-section-company-legalize-date-label',
  '_input-test-id': 'contract-credential-section-company-legalize-date-field',
  '_div-class': 'input-with-helper input-with-buttons',
  '_input-class': 'input-business-start-date',
  '_input-helper': '<small class="input-helper">Data w formacie DD.MM.RRRR</small>',
  'placeholder': 'Data rozpoczęcia działalności',
  'type': 'tel',
  'name': 'legalizeDate',
  'aria-required': 'true',
  'aria-describedby': 'date-format',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'businessStartDate',
  '_validators': [Validators.required, FormCustomValidators.businessStartDate],
});

const email = new FieldForm('email', {
  '_label': 'E-mail',
  '_label-test-id': 'contract-credential-section-email-label',
  '_input-test-id': 'contract-credential-section-email-field',
  '_input-helper': (commonInfo: CommonInfoService) => {
    let text = '<small class="input-helper" data-automation-id="contract-credential-section-email-info">';

    if (commonInfo.isOtherSalesOffer) {
     text += 'Na ten e-mail dostaniesz informacje o statusie zamówienia.';
    } else {
      text += 'Na ten e-mail dostaniesz informacje o statusie zamówienia oraz eFaktury.';
    }

    text += '</small>';

    return text;
  },
  'placeholder': 'E-mail',
  'type': 'text',
  'name': 'customerEmail',
  'maxlength': '140',
  'aria-required': 'true',
  'aria-describedby': 'info-mail',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'email',
  '_validators': [Validators.required, FormCustomValidators.email],
});

const phone = new FieldForm('phone', {
  '_label': 'Telefon kontaktowy',
  '_label-test-id': 'contract-credential-section-contact-phone-label',
  '_input-test-id': 'contract-credential-section-address-customer-phone-field',
  '_input-helper': '<small class="input-helper" data-automation-id="contract-credential-section-address-phone-info">Podaj numer abyśmy mogli się z tobą skontaktować w celu realizacji zamówienia. Ten numer posłuży również do kontaktu dla kuriera w momencie dostarczenia przesyłki.</small>',
  'placeholder': 'Telefon kontaktowy',
  'type': 'tel',
  'name': 'customerPhone',
  'aria-describedby': 'info-contact-phone',
  'formControlGroup': FormGroupsEnum.CUSTOMER_DETAILS,
  'formControlName': 'phone',
  '_validators': [Validators.required, FormCustomValidators.phone],
});

class FieldsFormGroup {
  uid: string;
  fields: {string?: FieldForm} = {};
  isEditable = true;

  constructor(uid: string, fieldsArray: FieldForm[]) {
    this.uid = uid;

    fieldsArray.forEach((field: FieldForm, key: number) => {
      this.fields[field.uid] = field;
    });
  }
}

/**
 * default field for contractAddress, deliveryAddress, correspondenceAddress and installationAddress
 */

const defaultFieldConfigForPostalCode = {
  '_label': 'Kod pocztowy',
  '_label-class': 'form-label',
  'type': 'tel',
  'placeholder': 'Kod pocztowy',
  'aria-required': 'true',
  '_validators': [Validators.required, FormCustomValidators.postalCode],
};

const defaultFieldConfigForCity = {
  '_label': 'Miejscowość',
  '_label-class': 'form-label',
  'type': 'text',
  'name': 'city',
  'maxlength':'35',
  'placeholder': 'Miejscowość',
  'aria-required': 'true',
  '_validators': [Validators.required, FormCustomValidators.city],
};

const defaultFieldConfigForProvince = {
  '_label': 'Województwo',
  '_label-class': 'form-label',
  '_div-class': 'input-with-helper province-field',
  'type': 'text',
  'name': 'province',
  'maxlength':'35',
  'placeholder': 'Województwo',
  'aria-required': 'true',
  "tabindex": '-1',
  '_validators': [Validators.required, FormCustomValidators.province],
};

const defaultFieldConfigForStreetName = {
  '_label': 'Ulica',
  '_label-class': 'form-label',
  '_input-helper': '<small class="input-helper">Jeśli miejscowość nie posiada ulic, proszę wpisać nazwę miejscowości.</small>',
  'type': 'text',
  'maxlength': '50',
  'placeholder': 'Ulica',
  'aria-required': 'true',
  '_validators': [Validators.required, FormCustomValidators.street],
};

const defaultFieldConfigForStreetNumber = {
  '_label': 'Numer domu',
  '_label-class': 'form-label',
  'type': 'text',
  'maxlength': '10',
  'placeholder': 'Nr domu',
  'aria-required': 'true',
  '_validators': [Validators.required, FormCustomValidators.streetNumber],
};

const defaultFieldConfigForAppartment = {
  '_label': 'Numer lokalu',
  '_label-class': 'form-label',
  'type': 'text',
  'maxlength': '10',
  'placeholder': 'Nr lokalu',
  '_validators': [FormCustomValidators.apartmentNumber],
};

/**
 * contractAddress
 */

const contractAddressPostalCode = new FieldForm('contract-postal-code', Object.assign({}, defaultFieldConfigForPostalCode, {
  '_input-test-id': 'contract-credential-section-address-postal-code-field',
  'name': 'contractAddress.postalcode',
  'formControlGroup': FormGroupsEnum.CONTRACT_ADDRESS,
  'formControlName': 'postalCode',
}));

const contractAddressTown = new FieldForm('contract-city', Object.assign({}, defaultFieldConfigForCity, {
  '_input-test-id': 'contract-credential-section-address-town-field',
  'name': 'contractAddress.town',
  'formControlGroup': FormGroupsEnum.CONTRACT_ADDRESS,
  'formControlName': 'city',
}));

const contractAddressProvince = new FieldForm('contract-province', Object.assign({}, defaultFieldConfigForProvince, {
  "_input-test-id": "contract-credential-section-address-province-field",
  "name": "contractAddress.province",
  "(change)": "userDataFormService.onContractAddressChange()",
  "formControlGroup": FormGroupsEnum.CONTRACT_ADDRESS,
  "formControlName": "province",
}));

const contractAddressStreetName = new FieldForm ('contract-street', Object.assign({}, defaultFieldConfigForStreetName, {
  '_input-test-id': 'contract-credential-section-address-streetname-field',
  'name': 'contractAddress.streetname',
  'formControlGroup': FormGroupsEnum.CONTRACT_ADDRESS,
  'formControlName': 'street',
}));

const contractAddressHouseNumber = new FieldForm('contract-house-number', Object.assign({}, defaultFieldConfigForStreetNumber, {
  '_input-test-id': 'contract-credential-section-address-streetnumber-field',
  'name': 'contractAddress.streetnumber',
  'formControlGroup': FormGroupsEnum.CONTRACT_ADDRESS,
  'formControlName': 'houseNumber',
}));

const contractAddressApartmentNumber = new FieldForm('contract-apartment-number', Object.assign({}, defaultFieldConfigForAppartment, {
  '_input-test-id': 'contract-credential-section-address-appartment-field',
  'name': 'contractAddress.appartment',
  'formControlGroup': FormGroupsEnum.CONTRACT_ADDRESS,
  'formControlName': 'apartmentNumber',
}));

/**
 * deliveryAddress
 */

const deliveryAddressPostalCode = new FieldForm('delivery-postal-code', Object.assign({}, defaultFieldConfigForPostalCode, {
  '_input-test-id': 'delivery-mode-section-other-address-zip-code-field',
  'name': 'deliveryAddress.postalcode',
  'formControlGroup': FormGroupsEnum.DELIVERY_ADDRESS,
  'formControlName': 'postalCode',
}));

const deliveryAddressTown = new FieldForm('delivery-city', Object.assign({}, defaultFieldConfigForCity, {
  '_input-test-id': 'delivery-mode-section-other-address-city-field',
  'name': 'deliveryAddress.town',
  'formControlGroup': FormGroupsEnum.DELIVERY_ADDRESS,
  'formControlName': 'city',
}));

const deliveryAddressProvince = new FieldForm('delivery-province', Object.assign({}, defaultFieldConfigForProvince, {
  "_input-test-id": "delivery-mode-section-other-address-province-field",
  "name": "deliveryAddress.province",
  "formControlGroup": FormGroupsEnum.DELIVERY_ADDRESS,
  "formControlName": "province",
}));

const deliveryAddressStreetName = new FieldForm ('delivery-street', Object.assign({}, defaultFieldConfigForStreetName, {
  '_input-test-id': 'delivery-mode-section-other-address-street-field',
  'name': 'deliveryAddress.streetname',
  'formControlGroup': FormGroupsEnum.DELIVERY_ADDRESS,
  'formControlName': 'street',
}));

const deliveryAddressStreetNumber = new FieldForm('delivery-house-number', Object.assign({}, defaultFieldConfigForStreetNumber, {
  '_input-test-id': 'delivery-mode-section-other-address-house-no-field',
  'name': 'deliveryAddress.streetnumber',
  'formControlGroup': FormGroupsEnum.DELIVERY_ADDRESS,
  'formControlName': 'houseNumber',
}));

const deliveryAddressApartmentNumber = new FieldForm('delivery-apartment-number', Object.assign({}, defaultFieldConfigForAppartment, {
  '_input-test-id': 'delivery-mode-section-other-address-appartment-number-field',
  'name': 'deliveryAddress.appartment',
  'formControlGroup': FormGroupsEnum.DELIVERY_ADDRESS,
  'formControlName': 'apartmentNumber',
}));

/**
 * correspondenceAddress
 */

const correspondenceAddressPostalCode = new FieldForm('correspondence-postal-code', Object.assign({}, defaultFieldConfigForPostalCode, {
  '_input-test-id': 'contract-credential-section-correspondence-address-postalcode-field',
  'name': 'correspondenceAddress.postalcode',
  'formControlGroup': FormGroupsEnum.CORRESPONDENCE_ADDRESS,
  'formControlName': 'postalCode',
}));

const correspondenceAddressTown = new FieldForm('correspondence-city', Object.assign({}, defaultFieldConfigForCity, {
  '_input-test-id': 'contract-credential-section-correspondence-address-town-field',
  'name': 'correspondenceAddress.town',
  'formControlGroup': FormGroupsEnum.CORRESPONDENCE_ADDRESS,
  'formControlName': 'city',
}));

const correspondenceAddressProvince = new FieldForm('correspondence-province', Object.assign({}, defaultFieldConfigForProvince, {
  "_input-test-id": "contract-credential-section-correspondence-address-province-field",
  "name": "correspondenceAddress.province",
  "(change)": "userDataFormService.onOtherCorrespondenceAddressInputChange($event)",
  "formControlGroup": FormGroupsEnum.CORRESPONDENCE_ADDRESS,
  "formControlName": "province",
}));

const correspondenceAddressStreetName = new FieldForm ('correspondence-street', Object.assign({}, defaultFieldConfigForStreetName, {
  '_input-test-id': 'contract-credential-section-correspondence-address-street-name-field',
  'name': 'correspondenceAddress.streetname',
  'formControlGroup': FormGroupsEnum.CORRESPONDENCE_ADDRESS,
  'formControlName': 'street',
}));

const correspondenceAddressStreetNumber = new FieldForm('correspondence-house-number', Object.assign({}, defaultFieldConfigForStreetNumber, {
  '_input-test-id': 'contract-credential-section-correspondence-address-street-number-field',
  'name': 'correspondenceAddress.streetnumber',
  'formControlGroup': FormGroupsEnum.CORRESPONDENCE_ADDRESS,
  'formControlName': 'houseNumber',
}));

const correspondenceAddressApartmentNumber = new FieldForm('correspondence-apartment-number', Object.assign({}, defaultFieldConfigForAppartment, {
  '_input-test-id': 'contract-credential-section-correspondence-address-appartment-field',
  'name': 'correspondenceAddress.appartment',
  'formControlGroup': FormGroupsEnum.CORRESPONDENCE_ADDRESS,
  'formControlName': 'apartmentNumber',
}));

/**
 * installationAddress
 */

const installationAddressPostalCode = new FieldForm('installation-postal-code', Object.assign({}, defaultFieldConfigForPostalCode, {
  '_input-test-id': 'contract-credential-section-installation-address-postalcode-field',
  'name': 'installationAddress.postalcode',
  'formControlGroup': FormGroupsEnum.INSTALLATION_ADDRESS,
  'formControlName': 'postalCode',
}));

const installationAddressTown = new FieldForm('installation-city', Object.assign({}, defaultFieldConfigForCity, {
  '_input-test-id': 'contract-credential-section-installation-address-town-field',
  'name': 'installationAddress.town',
  'formControlGroup': FormGroupsEnum.INSTALLATION_ADDRESS,
  'formControlName': 'city',
}));

const installationAddressProvince = new FieldForm('installation-province', Object.assign({}, defaultFieldConfigForProvince, {
  "_input-test-id": "contract-credential-section-installation-address-province-field",
  "name": "installationAddress.province",
  "(change)": "userDataFormService.onOtherInstallationAddressInputChange($event)",
  "formControlGroup": FormGroupsEnum.INSTALLATION_ADDRESS,
  "formControlName": "province",
}));

const installationAddressStreetName = new FieldForm ('installation-street', Object.assign({}, defaultFieldConfigForStreetName, {
  '_input-test-id': 'contract-credential-section-installation-address-street-name-field',
  'name': 'installationAddress.streetname',
  'formControlGroup': FormGroupsEnum.INSTALLATION_ADDRESS,
  'formControlName': 'street',
}));

const installationAddressStreetNumber = new FieldForm('installation-house-number', Object.assign({}, defaultFieldConfigForStreetNumber, {
  '_input-test-id': 'contract-credential-section-installation-address-street-number-field',
  'name': 'installationAddress.streetnumber',
  'formControlGroup': FormGroupsEnum.INSTALLATION_ADDRESS,
  'formControlName': 'houseNumber',
}));

const installationAddressApartmentNumber = new FieldForm('installation-apartment-number', Object.assign({}, defaultFieldConfigForAppartment, {
  '_input-test-id': 'contract-credential-section-installation-address-appartment-field',
  'name': 'installationAddress.appartment',
  'formControlGroup': FormGroupsEnum.INSTALLATION_ADDRESS,
  'formControlName': 'apartmentNumber',
}));


const contractAddress = new FieldsFormGroup(FormGroupsEnum.CONTRACT_ADDRESS, [contractAddressPostalCode, contractAddressTown, contractAddressProvince, contractAddressStreetName, contractAddressHouseNumber, contractAddressApartmentNumber]);
const deliveryAddress = new FieldsFormGroup(FormGroupsEnum.DELIVERY_ADDRESS, [deliveryAddressPostalCode, deliveryAddressTown, deliveryAddressProvince, deliveryAddressStreetName, deliveryAddressStreetNumber, deliveryAddressApartmentNumber]);
const correspondenceAddress = new FieldsFormGroup(FormGroupsEnum.CORRESPONDENCE_ADDRESS, [correspondenceAddressPostalCode, correspondenceAddressTown, correspondenceAddressProvince, correspondenceAddressStreetName, correspondenceAddressStreetNumber, correspondenceAddressApartmentNumber]);
const installationAddress = new FieldsFormGroup(FormGroupsEnum.INSTALLATION_ADDRESS, [installationAddressPostalCode, installationAddressTown, installationAddressProvince, installationAddressStreetName, installationAddressStreetNumber, installationAddressApartmentNumber]);


class Form {
  public b2c: object = {};
  public b2b: object = {};
  public common: object = {};

  addField(field: FieldForm, restriction: 'b2c' | 'b2b' | 'common' = 'common', forceHide = false, isEditable = true) {
    field['forceHide'] = forceHide;
    field['isEditable'] = isEditable;
    field['label']['_isEditable'] = isEditable;
    field['input']['_isEditable'] = isEditable;

    this[restriction][field.uid] = field;

    return this;
  }

  addFieldsGroup(fieldsGroup: FieldsFormGroup, restriction: 'b2c' | 'b2b' | 'common' = 'common', forceHide = false, isEditable = true) {
    fieldsGroup['forceHide'] = forceHide;
    fieldsGroup['isEditable'] = isEditable;

    Object.keys(fieldsGroup.fields).forEach((names: string) => {
      fieldsGroup.fields[names].isEditable = isEditable;
      fieldsGroup.fields[names].label._isEditable = isEditable;
      fieldsGroup.fields[names].input._isEditable = isEditable;
    });

    this[restriction][fieldsGroup.uid] = fieldsGroup;

    return this;
  }
}

@Injectable()
export default class FormFieldsSchemaService {
  public fields: object = {};
  public tempFields: Form;

  constructor(
    private commonInfo: CommonInfoService,
    private notificationProvider: NotificationProviderService
  ){
    this.formFieldsConfiguration(commonInfo.mainOfferProcessType);

    this.switchFormFieldsToOfferType();
  }

  formFieldsConfiguration(processType: string): void {
    switch (processType) {
      case (MainProcessTypeEnum.PREPAID_2_POSTPAID):
        this.tempFields = new Form()
          .addField(
            firstName,
            'common',
            false,
            false
          )
          .addField(
            lastName,
            'common',
            false,
            false
          )
          .addField(
            pesel,
            'common',
            false,
            false
          )
          .addField(
            mIdNumber
          )
          .addField(
            mIdExpiryDate,
            'common'
          )
          .addField(
            idSeriesNumber
          )
          .addField(
            idExpiryDate,
            'common'
          )
          .addField(
            indefiniteId,
            'common'
          )
          .addField(
            passwordForContract
          )
          .addField(
            companyName,
            'b2b'
          )
          .addField(
            nip,
            'b2b'
          )
          .addField(
            regon,
            'b2b'
          )
          .addField(
            legalizeDate,
            'b2b'
          )
          .addField(
            email
          )
          .addField(
            phone
          )
          .addFieldsGroup(
            contractAddress,
            'common'
          )
          .addFieldsGroup(
            deliveryAddress,
            'common'
          )
          .addFieldsGroup(
            correspondenceAddress,
            'common'
          );
        break;

      case MainProcessTypeEnum.OTHER_SALES:
        this.tempFields = new Form()
          .addField(
            firstName
          )
          .addField(
            lastName
          )
          .addField(
            companyName,
            'b2b'
          )
          .addField(
            nip,
            'b2b'
          )
          .addField(
            email
          )
          .addField(
            phone
          )
          .addFieldsGroup(
            contractAddress,
            'b2b'
          )
          .addFieldsGroup(
            deliveryAddress,
            'b2b',
            true
          );
        break;

      default:
        this.tempFields = new Form()
          .addField(
            firstName
          )
          .addField(
            lastName
          )
          .addField(
            pesel
          )
          .addField(
            mIdNumber
          )
          .addField(
            idSeriesNumber
          )
          .addField(
            mIdExpiryDate
          )
          .addField(
            idExpiryDate
          )
          .addField(
            indefiniteId,
            'common'
          )
          .addField(
            passwordForContract
          )
          .addField(
            companyName,
            'b2b'
          )
          .addField(
            nip,
            'b2b',
          )
          .addField(
            regon,
            'b2b'
          )
          .addField(
            legalizeDate,
            'b2b'
          )
          .addField(
            email
          )
          .addField(
            phone
          )
          .addFieldsGroup(
            contractAddress,
            'common'
          )
          .addFieldsGroup(
            deliveryAddress,
            'common',
            true
          )
          .addFieldsGroup(
            correspondenceAddress,
            'common',
            true
          )
          .addFieldsGroup(
            installationAddress,
            'common',
            true
          );
    }
  }

  switchFormFieldsToOfferType(): void {
    this.notificationProvider.subscribe(
      NotificationProviderTypeEnum.COMMON_INFO,
      (commonInfo: CommonInfoService) => {
        if (!commonInfo) {
          return;
        }

        this.setFormFields();
      }
    );
  }

  setFormFields(): void {
    /*
     * Important! Keep object reference.
     */

    Object.assign(this.fields, this.tempFields.common, this.tempFields.b2c, this.tempFields.b2b);

    this.notificationProvider.publish(NotificationProviderTypeEnum.FORM_TEMP_FIELDS_CONFIGURATION, this.tempFields);
    this.notificationProvider.publish(NotificationProviderTypeEnum.FORM_FIELDS_CONFIGURATION, this.fields);
  }
}

